import type { ReceiptCategory } from '../types';
import { extractContactsFromText } from './contactExtraction';

export interface ParsedReceipt {
  merchant: string;
  amount: string;
  currency: string;
  category: ReceiptCategory;
  date: string;
  rawText: string;
  note?: string;
  contact_emails: string[];
  contact_websites: string[];
}

function normalizeAmount(s: string): string {
  const cleaned = s.replace(/[^\d.,]/g, '').replace(',', '.');
  const n = parseFloat(cleaned);
  if (Number.isNaN(n)) return '0.00';
  return n.toFixed(2);
}

/** Heuristic parse of noisy OCR / PDF text into receipt fields */
export function parseReceiptText(
  text: string,
  fileLabel: string
): Omit<ParsedReceipt, 'rawText' | 'note' | 'contact_emails' | 'contact_websites'> {
  const flat = text.replace(/\s+/g, ' ').trim();
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  let amount = '0.00';
  let currency = 'EUR';

  const patterns: RegExp[] = [
    /(?:total|amount\s+due|balance\s+due|sum\s+payable|grand\s+total|to\s+pay|maksu|yhteensä|summa)[:\s]*([€$£]?\s*[\d\s.,]+)/i,
    /(?:^|\s)([€$£]\s*\d+[.,]\d{2})\b/,
    /\b(\d+[.,]\d{2})\s*(€|EUR|USD|\$)\b/i,
    /\b(?:EUR|USD)\s*(\d+[.,]\d{2})\b/i,
  ];

  for (const re of patterns) {
    const m = flat.match(re);
    if (m) {
      const g1 = m[1] || m[2];
      const g2 = m[2];
      if (g1) {
        amount = normalizeAmount(g1);
        if (g2 && (g2.includes('$') || g2 === 'USD')) currency = 'USD';
        else if (g2 && (g2.includes('£') || g2 === 'GBP')) currency = 'GBP';
        break;
      }
    }
  }

  if (amount === '0.00') {
    const loose = flat.match(/(\d+[.,]\d{2})\b/g);
    if (loose?.length) {
      const nums = loose.map((x) => parseFloat(x.replace(',', '.'))).filter((n) => !Number.isNaN(n));
      if (nums.length) {
        amount = Math.max(...nums).toFixed(2);
      }
    }
  }

  let merchant = lines.find((l) => l.length >= 3 && l.length < 80 && !/^\d+[.,]\d{2}$/.test(l)) || fileLabel;
  merchant = merchant.replace(/^[\d\s.,:-]+/, '').trim() || fileLabel;
  if (merchant.length > 72) merchant = `${merchant.slice(0, 69)}…`;

  const low = flat.toLowerCase();
  let category: ReceiptCategory = 'other';
  if (/hotel|lodging|hostel|airbnb|booking\.|motel|resort|yöpy|hotelli/.test(low)) {
    category = 'lodging';
  } else if (
    /boarding pass|boarding|gate\b|terminal\b|finnair|norwegian|\bsas\b|lento|lennon|e-ticket|flight\s*#|pnr|departure|arrival.*flight/i.test(
      flat
    ) ||
    /\b[A-Z]{2}\s?\d{3,4}\b/.test(flat)
  ) {
    category = 'flight';
  } else if (/vr\b|pendolino|intercity|juna|railway|train ticket|ic\s*\d|sj\b|db\b.*ticket/i.test(low)) {
    category = 'train';
  } else if (/matkahuolto|coach|bus ticket|long.?distance bus/i.test(low)) {
    category = 'bus';
  } else if (/ferry|tallink|viking line|silja|wasaline|stena|lautta/i.test(low)) {
    category = 'ferry';
  } else if (
    /restaurant|cafe|coffee|food|market|grocery|starbucks|mcdonald|k-market|s-market|prisma|lidl|ruoka/.test(low)
  ) {
    category = 'food';
  } else if (/uber|bolt|taxi|fuel|parking|metro|tram|car rental|rental car|drive/.test(low)) {
    category = 'transport';
  }

  let date = new Date().toISOString().slice(0, 10);
  const dm = flat.match(/\b(\d{4}-\d{2}-\d{2})\b|\b(\d{1,2}[./-]\d{1,2}[./-]\d{2,4})\b/);
  if (dm) {
    const raw = dm[1] || dm[2];
    const tryD = new Date(raw);
    if (!Number.isNaN(tryD.getTime())) {
      date = tryD.toISOString().slice(0, 10);
    }
  }

  return { merchant, amount, currency, category, date };
}

/**
 * Point pdf.js at a worker matching the installed `pdfjs-dist` (uses bundled `version`, not a hardcoded string).
 * Avoids: API version "x" does not match Worker version "y".
 */
export async function configurePdfJsWorker(): Promise<typeof import('pdfjs-dist')> {
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
  return pdfjs;
}

async function extractPdfText(file: File): Promise<string> {
  const pdfjs = await configurePdfJsWorker();

  const buf = await file.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data: buf, useSystemFonts: true });
  const doc = await loadingTask.promise;
  let full = '';
  const maxPages = Math.min(doc.numPages, 24);
  for (let i = 1; i <= maxPages; i++) {
    const page = await doc.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = textContent.items
      .map((item) => {
        if (item && typeof item === 'object' && 'str' in item && typeof (item as { str: string }).str === 'string') {
          return (item as { str: string }).str;
        }
        return '';
      })
      .join(' ');
    full += `${pageText}\n`;
  }
  return full;
}

/** If below this (non-whitespace chars), also rasterize pages and OCR — image-only PDFs have no text layer */
const MIN_PDF_TEXT_FOR_SKIP_OCR = 160;
const MAX_PDF_OCR_PAGES = 8;

/**
 * Boarding passes / scans often have no extractable text; pdf.js returns empty while routes are visible on the image.
 */
async function extractPdfTextWithOcrFallback(
  file: File,
  onProgress?: (msg: string) => void
): Promise<string> {
  const textLayer = await extractPdfText(file);
  const significant = textLayer.replace(/\s/g, '').length;
  if (significant >= MIN_PDF_TEXT_FOR_SKIP_OCR) {
    return textLayer;
  }

  onProgress?.('Little or no selectable text in PDF — reading pages with OCR…');

  const pdfjs = await configurePdfJsWorker();

  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf, useSystemFonts: true }).promise;
  const maxPages = Math.min(doc.numPages, MAX_PDF_OCR_PAGES);

  const { createWorker } = await import('tesseract.js');
  const worker = await createWorker('eng', 1, {
    logger: (m: { status?: string; progress?: number }) => {
      if (m.status === 'recognizing text' && typeof m.progress === 'number') {
        onProgress?.(`OCR PDF ${Math.round(m.progress * 100)}%`);
      }
    },
  });

  let ocrCombined = '';
  try {
    for (let i = 1; i <= maxPages; i++) {
      onProgress?.(`OCR PDF page ${i} of ${maxPages}…`);
      const page = await doc.getPage(i);
      const base = page.getViewport({ scale: 1 });
      const scale = Math.min(2.5, (1200 / base.width) * 1);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      await page.render({ canvasContext: ctx, viewport }).promise;
      const {
        data: { text },
      } = await worker.recognize(canvas);
      ocrCombined += `${text || ''}\n`;
    }
  } finally {
    await worker.terminate();
  }

  const ocr = ocrCombined.trim();
  const layer = textLayer.trim();
  if (!ocr) return layer;
  if (!layer) return ocr;
  return `${layer}\n\n${ocr}`;
}

async function extractImageText(
  file: File,
  onProgress?: (msg: string) => void
): Promise<string> {
  const { createWorker } = await import('tesseract.js');
  onProgress?.('Running OCR (first run may download language data)…');
  const worker = await createWorker('eng', 1, {
    logger: (m: { status?: string; progress?: number }) => {
      if (m.status === 'recognizing text' && typeof m.progress === 'number') {
        onProgress?.(`OCR ${Math.round(m.progress * 100)}%`);
      }
    },
  });
  try {
    const {
      data: { text },
    } = await worker.recognize(file);
    return text || '';
  } finally {
    await worker.terminate();
  }
}

const MAX_STORE_BYTES = 4 * 1024 * 1024;

export async function analyzeReceiptFile(
  file: File,
  onProgress?: (msg: string) => void
): Promise<ParsedReceipt> {
  const name = file.name || 'receipt';
  const label = name.replace(/\.[^.]+$/, '') || 'Receipt';
  const mime = file.type || '';

  let rawText = '';
  const lower = name.toLowerCase();
  const isPdf = mime === 'application/pdf' || lower.endsWith('.pdf');

  try {
    if (isPdf) {
      onProgress?.('Extracting text from PDF…');
      rawText = await extractPdfTextWithOcrFallback(file, onProgress);
    } else if (mime.startsWith('image/') || /\.(png|jpe?g|webp|gif|bmp|tif)$/i.test(lower)) {
      rawText = await extractImageText(file, onProgress);
    } else {
      return {
        ...parseReceiptText('', label),
        rawText: '',
        note: 'Unsupported file type for analysis.',
        contact_emails: [],
        contact_websites: [],
      };
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return {
      ...parseReceiptText('', label),
      rawText: '',
      note: `Analysis error: ${msg}`,
      contact_emails: [],
      contact_websites: [],
    };
  }

  if (!rawText.trim()) {
    const parsed = parseReceiptText('', label);
    return {
      ...parsed,
      rawText: '',
      note: 'No text found — values are guesses; edit in a future version or re-scan.',
      contact_emails: [],
      contact_websites: [],
    };
  }

  const parsed = parseReceiptText(rawText, label);
  const cap = 48000;
  const fullForContacts = rawText;
  const contacts = extractContactsFromText(fullForContacts);
  return {
    ...parsed,
    rawText: rawText.length > cap ? rawText.slice(0, cap) : rawText,
    contact_emails: contacts.emails,
    contact_websites: contacts.websites,
  };
}

export function readFileAsDataUrlIfSmall(file: File): Promise<string | null> {
  if (file.size > MAX_STORE_BYTES) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

export { MAX_STORE_BYTES };
